import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useContent, formatPrice } from "@/context/ContentContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import ImageInput from "@/components/ImageInput";
import {
  Plus, Trash2, Pencil, Package, ShoppingBag, Users, IndianRupee, RotateCcw, Save,
  Upload, ChevronDown, ChevronRight, Puzzle, CheckCircle2, Circle, X, MapPin, Tag,
} from "lucide-react";

const EMPTY_PRODUCT = {
  name: "", slug: "", category: "hospital-beds", description: "",
  factory_price: 0, mrp: 0, stock: 0, image: "", images: [], specs: {}, featured: false,
  weight_kg: 0, length_cm: 0, width_cm: 0, height_cm: 0, hsn_code: "9402", gst_rate: 12, bulk_qty: 0,
};

const STATUSES = ["pending", "approved", "processing", "shipped", "delivered", "cancelled", "refunded"];
const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  approved: "bg-blue-100 text-tmf-blue",
  processing: "bg-sky-100 text-sky-700",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-amber-100 text-amber-700",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
}

export default function AdminDashboard() {
  const { content, reload, save } = useContent();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [contentDraft, setContentDraft] = useState(null);
  const [contentBusy, setContentBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [custDialogOpen, setCustDialogOpen] = useState(false);
  const [pincodesText, setPincodesText] = useState("");
  const [pincodeList, setPincodeList] = useState({ total: 0, pincodes: [] });
  const [pinBusy, setPinBusy] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", slug: "", icon: "Package", description: "" });
  const [catBusy, setCatBusy] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [enquiryFilter, setEnquiryFilter] = useState("all");
  const [editingOrderCust, setEditingOrderCust] = useState(null);
  const [orderCustBusy, setOrderCustBusy] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: "Home", name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "", country: "India", is_default: false });
  const [addressBusy, setAddressBusy] = useState(false);

  const loadAll = async () => {
    const [s, p, o, c, cust, integ, pins, enq] = await Promise.all([
      api.get("/admin/stats"), api.get("/products?limit=200"),
      api.get("/admin/orders"), api.get("/categories"),
      api.get("/admin/customers"), api.get("/admin/integrations"),
      api.get("/admin/pincodes?limit=1000"),
      api.get("/admin/enquiries"),
    ]);
    setStats(s.data); setProducts(p.data); setOrders(o.data); setCategories(c.data);
    setCustomers(cust.data); setIntegrations(integ.data); setPincodeList(pins.data);
    setEnquiries(enq.data);
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (content && !contentDraft) setContentDraft(JSON.parse(JSON.stringify(content))); }, [content]);

  const openNew = () => {
    setEditing({ ...EMPTY_PRODUCT, specsList: [] });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    const specsList = Object.entries(p.specs || {}).map(([k, v]) => ({
      id: Math.random().toString(36).slice(2, 10), k, v: String(v ?? ""),
    }));
    setEditing({ ...p, images: p.images || [], specs: p.specs || {}, specsList });
    setDialogOpen(true);
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    setUploadBusy(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      return data.url;
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Upload failed");
      return null;
    } finally { setUploadBusy(false); }
  };

  const saveProduct = async () => {
    try {
      const body = { ...editing,
        factory_price: Number(editing.factory_price),
        mrp: Number(editing.mrp),
        stock: Number(editing.stock),
        weight_kg: Number(editing.weight_kg || 0),
        length_cm: Number(editing.length_cm || 0),
        width_cm: Number(editing.width_cm || 0),
        height_cm: Number(editing.height_cm || 0),
        gst_rate: Number(editing.gst_rate || 12),
        bulk_qty: Number(editing.bulk_qty || 0),
        // Convert specsList array → specs object, stripping empty/blank-key rows.
        // Later rows with duplicate keys overwrite earlier ones (matches user expectation).
        specs: Object.fromEntries(
          (editing.specsList || [])
            .filter((r) => (r.k || "").trim() && String(r.v ?? "").trim())
            .map((r) => [r.k.trim(), String(r.v).trim()])
        ),
      };
      // Drop the transient editing-state field before persisting
      delete body.specsList;
      if (!body.slug) body.slug = body.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      if (!body.image) return toast.error("Image required (upload or paste URL)");
      if (editing.product_id) {
        await api.put(`/admin/products/${editing.product_id}`, body);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", body);
        toast.success("Product created");
      }
      setDialogOpen(false); setEditing(null); loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Save failed"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Delete failed"); }
  };

  const updateOrderStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); toast.success(`Order → ${status}`); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Update failed"); }
  };

  const shipViaShiprocket = async (id) => {
    if (!window.confirm(`Push order ${id} to Shiprocket now?\n\nThis creates the shipment + assigns the cheapest available courier + generates an AWB. Make sure the customer's pincode is serviceable and the product dimensions are set correctly.`)) return;
    try {
      const { data } = await api.post(`/admin/orders/${id}/ship`);
      toast.success(data.awb ? `Shipped · AWB ${data.awb} via ${data.courier_name || "courier"}` : "Shipment created — AWB pending (assign manually in Shiprocket)");
      loadAll();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Shiprocket push failed");
    }
  };

  const shipViaCargo = async (id) => {
    if (!window.confirm(`Push order ${id} to Shiprocket Cargo?\n\nThis creates a heavy-shipment cargo booking (auto-assigns the cheapest carrier). Use this when cart weight > 10 kg.`)) return;
    try {
      const { data } = await api.post(`/admin/orders/${id}/ship_cargo`);
      toast.success(data.waybill_no ? `Cargo booked · Waybill ${data.waybill_no}` : `Cargo order created (ID ${data.cargo_order_id || "—"}). Waybill will appear after courier assigns.`);
      loadAll();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Cargo push failed");
    }
  };

  const refreshTracking = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/track`);
      toast.success(`Tracking synced · ${data.raw_status || data.status}`);
      loadAll();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Tracking sync failed");
    }
  };

  const saveCustomer = async () => {
    try {
      const body = { name: editingCustomer.name, role: editingCustomer.role, phone: editingCustomer.phone || "", company: editingCustomer.company || "", notes: editingCustomer.notes || "" };
      await api.put(`/admin/customers/${editingCustomer.user_id}`, body);
      toast.success("Customer updated");
      setCustDialogOpen(false); setEditingCustomer(null); loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Save failed"); }
  };

  const [integDialog, setIntegDialog] = useState(null); // holds the integration being edited
  const [integForm, setIntegForm] = useState({});
  const [integBusy, setIntegBusy] = useState(false);

  const openIntegration = (integ) => {
    const form = {};
    (integ.fields || []).forEach((f) => { form[f.key] = integ.config?.[f.key] || ""; });
    setIntegForm({ ...form, __enabled: !!integ.enabled });
    setIntegDialog(integ);
  };

  const saveIntegration = async () => {
    const { __enabled, ...cfg } = integForm;
    setIntegBusy(true);
    try {
      await api.put(`/admin/integrations/${integDialog.id}`, { enabled: __enabled, config: cfg });
      toast.success(`${integDialog.name} saved`);
      setIntegDialog(null);
      loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Save failed"); }
    finally { setIntegBusy(false); }
  };

  const resetIntegration = async (id) => {
    if (!window.confirm("Reset this integration? All saved keys will be removed.")) return;
    try { await api.delete(`/admin/integrations/${id}`); toast.success("Reset"); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const toggleIntegration = async (id, currentlyEnabled) => {
    try {
      const { data } = await api.post(`/admin/integrations/${id}/toggle`);
      toast.success(data.enabled ? "Connected" : "Disconnected");
      loadAll();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Toggle failed");
    }
  };

  const [srTesting, setSrTesting] = useState(false);
  const [srDiag, setSrDiag] = useState(null);
  const testShiprocket = async (endpoint = "/admin/shiprocket/test") => {
    setSrTesting(true); setSrDiag(null);
    try {
      const { data } = await api.post(endpoint);
      setSrDiag(data);
      if (data.ok) toast.success("Connection verified");
      else toast.error("Test failed — see details below");
    } catch (er) {
      setSrDiag({ ok: false, step: "network", message: formatApiError(er.response?.data?.detail) || String(er) });
      toast.error("Test failed");
    } finally {
      setSrTesting(false);
    }
  };

  // ---- Categories CRUD ----
  const addCategory = async () => {
    if (!newCat.name.trim()) { toast.error("Enter a category name"); return; }
    setCatBusy(true);
    try {
      await api.post("/admin/categories", newCat);
      toast.success(`Category "${newCat.name}" added`);
      setNewCat({ name: "", slug: "", icon: "Package", description: "" });
      loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed to add"); }
    finally { setCatBusy(false); }
  };

  const saveCategoryEdit = async () => {
    if (!editingCat) return;
    setCatBusy(true);
    try {
      await api.put(`/admin/categories/${editingCat.slug}`, {
        name: editingCat.name, icon: editingCat.icon, description: editingCat.description,
      });
      toast.success("Category updated");
      setEditingCat(null);
      loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Save failed"); }
    finally { setCatBusy(false); }
  };

  const deleteCategory = async (slug, name) => {
    if (!window.confirm(`Delete category "${name}"? Products using it must be reassigned first.`)) return;
    try { await api.delete(`/admin/categories/${slug}`); toast.success("Category deleted"); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm(`Delete order ${orderId}?\n\nThis removes the order and its payment transactions permanently. Refunds (if any) must be handled separately in the payment gateway.`)) return;
    try { await api.delete(`/admin/orders/${orderId}`); toast.success("Order deleted"); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const deleteCustomer = async (userId, name) => {
    if (!window.confirm(`Delete customer "${name}"?\n\nAny orders they placed will be kept as guest records for audit history. This cannot be undone.`)) return;
    try { await api.delete(`/admin/customers/${userId}`); toast.success("Customer deleted"); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const saveOrderCustomer = async () => {
    if (!editingOrderCust) return;
    setOrderCustBusy(true);
    try {
      await api.put(`/admin/orders/${editingOrderCust.order_id}/customer`, {
        email: editingOrderCust.email || "",
        name: editingOrderCust.name || "",
        phone: editingOrderCust.phone || "",
        address_line1: editingOrderCust.address_line1 || "",
        address_line2: editingOrderCust.address_line2 || "",
        city: editingOrderCust.city || "",
        state: editingOrderCust.state || "",
        postal_code: editingOrderCust.postal_code || "",
        country: editingOrderCust.country || "India",
      });
      toast.success("Order customer details updated");
      setEditingOrderCust(null);
      loadAll();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Update failed");
    } finally {
      setOrderCustBusy(false);
    }
  };

  const addCustomerAddress = async () => {
    if (!editingCustomer || !newAddress.address_line1 || !newAddress.city || !newAddress.postal_code) {
      toast.error("Fill address line 1, city, and pincode");
      return;
    }
    setAddressBusy(true);
    try {
      await api.post(`/admin/customers/${editingCustomer.user_id}/addresses`, newAddress);
      toast.success("Address added");
      // Refresh the editing customer's addresses
      const cust = await api.get("/admin/customers");
      const updated = cust.data.find((c) => c.user_id === editingCustomer.user_id);
      setEditingCustomer(updated);
      setCustomers(cust.data);
      setNewAddress({ label: "Home", name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "", country: "India", is_default: false });
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Failed to add address");
    } finally {
      setAddressBusy(false);
    }
  };

  const deleteCustomerAddress = async (addressId) => {
    if (!editingCustomer || !window.confirm("Delete this address?")) return;
    try {
      await api.delete(`/admin/customers/${editingCustomer.user_id}/addresses/${addressId}`);
      const cust = await api.get("/admin/customers");
      const updated = cust.data.find((c) => c.user_id === editingCustomer.user_id);
      setEditingCustomer(updated);
      setCustomers(cust.data);
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Delete failed");
    }
  };

  const setEnquiryStatus = async (id, status) => {
    try {
      await api.put(`/admin/enquiries/${id}`, { status });
      toast.success(`Marked ${status}`);
      loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const bulkAddPincodes = async (replace = false) => {
    if (!pincodesText.trim()) { toast.error("Paste at least one pincode"); return; }
    setPinBusy(true);
    try {
      const { data } = await api.post("/admin/pincodes/bulk", { pincodes: pincodesText, replace });
      toast.success(`Added ${data.added} new · ${data.total} total serviceable`);
      setPincodesText("");
      loadAll();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
    finally { setPinBusy(false); }
  };

  const removePincode = async (pin) => {
    try { await api.delete(`/admin/pincodes/${pin}`); toast.success("Removed"); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const clearAllPincodes = async () => {
    if (!window.confirm("Clear ALL pincodes? Site will fall back to pan-India serviceability.")) return;
    try { await api.delete("/admin/pincodes"); toast.success("Cleared"); loadAll(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Failed"); }
  };

  const onCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setPincodesText((prev) => (prev ? prev + "\n" : "") + text);
    e.target.value = "";
    toast.success("Loaded from file — click Add to save");
  };

  // CMS helpers
  const setPath = (path) => (val) => {
    setContentDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (typeof cur[parts[i]] !== "object" || cur[parts[i]] == null) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = val;
      return next;
    });
  };

  const setArrayField = (path, index, key, val) => {
    setContentDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let cur = next;
      for (const p of parts) cur = cur[p];
      cur[index][key] = val;
      return next;
    });
  };

  const saveContent = async () => {
    setContentBusy(true);
    try { await save(contentDraft); toast.success("Site content updated"); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Save failed"); }
    finally { setContentBusy(false); }
  };

  const resetContent = async () => {
    if (!window.confirm("Reset all site content to defaults?")) return;
    setContentBusy(true);
    try {
      await api.post("/admin/content/reset");
      await reload();
      setContentDraft(null);
      toast.success("Content reset to defaults");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Reset failed"); }
    finally { setContentBusy(false); }
  };

  const InputRow = ({ label, path, textarea = false, rows = 3 }) => {
    const parts = path.split(".");
    let v = contentDraft;
    for (const p of parts) v = v?.[p];
    const val = v == null ? "" : String(v);
    return (
      <div>
        <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">{label}</label>
        {textarea ? (
          <Textarea value={val} onChange={(e) => setPath(path)(e.target.value)} className="rounded-none mt-1 border-slate-300" rows={rows} />
        ) : (
          <Input value={val} onChange={(e) => setPath(path)(e.target.value)} className="rounded-none mt-1 border-slate-300" />
        )}
      </div>
    );
  };

  const currencySymbol = content?.currency?.symbol || "₹";

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="border-b border-slate-200 pb-6 mb-8 flex justify-between items-end">
        <div>
          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Admin</div>
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight text-tmf-navy">Command Center</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { icon: IndianRupee, label: "Revenue", value: `${currencySymbol}${stats.revenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
            { icon: ShoppingBag, label: "Total Orders", value: stats.total_orders },
            { icon: Package, label: "Products", value: stats.total_products },
            { icon: Users, label: "Customers", value: stats.total_customers },
          ].map((s) => (
            <div key={s.label} className="border border-slate-200 bg-white p-5">
              <s.icon size={20} className="text-tmf-blue mb-3" />
              <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">{s.label}</div>
              <div className="font-display font-black text-3xl mt-1 text-tmf-navy">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="rounded-none bg-slate-100 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-products" value="products" className="rounded-none uppercase tracking-widest text-xs font-bold">Products</TabsTrigger>
          <TabsTrigger data-testid="tab-categories" value="categories" className="rounded-none uppercase tracking-widest text-xs font-bold">Categories</TabsTrigger>
          <TabsTrigger data-testid="tab-orders" value="orders" className="rounded-none uppercase tracking-widest text-xs font-bold">Orders & Fulfillment</TabsTrigger>
          <TabsTrigger data-testid="tab-customers" value="customers" className="rounded-none uppercase tracking-widest text-xs font-bold">Customers</TabsTrigger>
          <TabsTrigger data-testid="tab-messages" value="messages" className="rounded-none uppercase tracking-widest text-xs font-bold">
            Messages{enquiries.filter((e) => e.status === "new").length > 0 && <span className="ml-2 bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">{enquiries.filter((e) => e.status === "new").length}</span>}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-pincodes" value="pincodes" className="rounded-none uppercase tracking-widest text-xs font-bold">Pincodes</TabsTrigger>
          <TabsTrigger data-testid="tab-content" value="content" className="rounded-none uppercase tracking-widest text-xs font-bold">Site Content</TabsTrigger>
          <TabsTrigger data-testid="tab-integrations" value="integrations" className="rounded-none uppercase tracking-widest text-xs font-bold">Integrations</TabsTrigger>
        </TabsList>

        {/* Products */}
        <TabsContent value="products">
          <div className="flex justify-between items-center mb-4 mt-6">
            <div className="text-sm font-mono-tmf uppercase tracking-widest text-slate-500">{products.length} products</div>
            <Button data-testid="new-product-btn" onClick={openNew} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold"><Plus size={14} className="mr-1" /> New Product</Button>
          </div>

          <div className="border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-tmf-navy text-white">
                <tr>
                  <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Product</th>
                  <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Category</th>
                  <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Price</th>
                  <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Stock</th>
                  <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.product_id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 flex items-center gap-3">
                      <img src={absUrl(p.image)} className="w-12 h-12 object-cover border border-slate-200" alt="" />
                      <div>
                        <div className="font-bold text-tmf-navy">{p.name}</div>
                        <div className="text-[10px] font-mono-tmf text-slate-500 uppercase">{p.product_id}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{p.category}</td>
                    <td className="px-3 py-2 text-right font-mono-tmf font-bold">{currencySymbol}{p.factory_price.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono-tmf">{p.stock}</td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`edit-${p.product_id}`} onClick={() => openEdit(p)} className="p-2 hover:bg-tmf-navy hover:text-white"><Pencil size={14} /></button>
                      <button data-testid={`delete-${p.product_id}`} onClick={() => deleteProduct(p.product_id)} className="p-2 hover:bg-tmf-orange hover:text-white"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Categories */}
        <TabsContent value="categories">
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {/* Add new */}
            <div className="md:col-span-1 border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-1">
                <Tag size={16} className="text-tmf-blue" />
                <div className="font-display font-bold uppercase tracking-widest text-sm">Add Category</div>
              </div>
              <div className="text-xs text-slate-500 mb-4">New categories appear in the All Products dropdown and Shop by Category grid.</div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Name</label>
                  <Input data-testid="new-cat-name" value={newCat.name}
                    onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                    placeholder="e.g. Orthopedic Equipment" className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Slug (optional)</label>
                  <Input value={newCat.slug}
                    onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })}
                    placeholder="auto from name" className="rounded-none mt-1" />
                  <div className="text-[10px] text-slate-400 mt-1">URL: /category/{newCat.slug || (newCat.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "..."}</div>
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Icon (Lucide name)</label>
                  <Input value={newCat.icon}
                    onChange={(e) => setNewCat({ ...newCat, icon: e.target.value })}
                    placeholder="Package · Bed · Wrench · Activity" className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Description</label>
                  <Input value={newCat.description}
                    onChange={(e) => setNewCat({ ...newCat, description: e.target.value })}
                    placeholder="Short one-liner shown under name" className="rounded-none mt-1" />
                </div>
                <Button data-testid="add-cat-btn" disabled={catBusy} onClick={addCategory}
                  className="rounded-none w-full bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold">
                  <Plus size={14} className="mr-1" /> {catBusy ? "Saving…" : "Add Category"}
                </Button>
              </div>
            </div>

            {/* List existing */}
            <div className="md:col-span-2 border border-slate-200 bg-white overflow-x-auto">
              <div className="bg-tmf-navy text-white px-4 py-3 font-display font-bold uppercase tracking-widest text-xs flex justify-between">
                <span>All Categories</span>
                <span className="text-slate-300 font-normal">{categories.length} total</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Name</th>
                    <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Slug</th>
                    <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Description</th>
                    <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Products</th>
                    <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => {
                    const count = products.filter((p) => p.category === c.slug).length;
                    return (
                      <tr key={c.slug} data-testid={`cat-row-${c.slug}`} className="border-t border-slate-200 hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-tmf-navy">{c.name}</td>
                        <td className="px-3 py-2 font-mono-tmf text-xs text-slate-500">{c.slug}</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{c.description}</td>
                        <td className="px-3 py-2 text-right font-mono-tmf text-xs">{count}</td>
                        <td className="px-3 py-2 text-right space-x-1">
                          <button data-testid={`edit-cat-${c.slug}`} onClick={() => setEditingCat({ ...c })}
                            className="p-2 hover:bg-tmf-navy hover:text-white"><Pencil size={14} /></button>
                          <button data-testid={`delete-cat-${c.slug}`} onClick={() => deleteCategory(c.slug, c.name)}
                            className="p-2 hover:bg-red-600 hover:text-white"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {categories.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No categories yet — add one on the left.</div>}
            </div>
          </div>
        </TabsContent>

        {/* Orders & Fulfillment */}
        <TabsContent value="orders">
          <div className="border border-slate-200 bg-white mt-6">
            <div className="bg-tmf-navy text-white px-4 py-3 font-display font-bold uppercase tracking-widest text-xs flex justify-between">
              <span>Order Fulfillment</span>
              <span className="text-slate-300 font-normal">{orders.length} orders</span>
            </div>
            {orders.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">No orders yet.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {orders.map((o) => {
                  const expanded = expandedOrder === o.order_id;
                  return (
                    <div key={o.order_id} data-testid={`admin-order-${o.order_id}`}>
                      <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedOrder(expanded ? null : o.order_id)}>
                        <div className="md:col-span-1 flex items-center gap-2">
                          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Order</div>
                          <div className="font-mono-tmf font-bold text-sm text-tmf-navy">{o.order_id}</div>
                        </div>
                        <div className="md:col-span-3">
                          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Customer</div>
                          <div className="text-sm truncate">{o.email}</div>
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Date</div>
                          <div className="text-sm">{new Date(o.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Amount</div>
                          <div className="font-display font-black">{currencySymbol}{o.amount.toLocaleString("en-IN")}</div>
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <span className={`text-[10px] font-mono-tmf uppercase px-2 py-1 ${STATUS_COLORS[o.status] || STATUS_COLORS.pending}`}>{o.status}</span>
                          <span className={`text-[10px] font-mono-tmf uppercase px-2 py-1 ${STATUS_COLORS[o.payment_status] || STATUS_COLORS.pending}`}>{o.payment_status}</span>
                        </div>
                      </div>
                      {expanded && (
                        <div className="p-6 bg-slate-50 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 mb-2">Shipping</div>
                            <div className="text-sm space-y-0.5">
                              <div className="font-bold">{o.shipping?.name}</div>
                              <div>{o.shipping?.phone}</div>
                              <div>{o.shipping?.address_line1}</div>
                              {o.shipping?.address_line2 && <div>{o.shipping.address_line2}</div>}
                              <div>{o.shipping?.city}, {o.shipping?.state} {o.shipping?.postal_code}</div>
                              <div>{o.shipping?.country}</div>
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 mb-2">Items ({o.items.length})</div>
                            <div className="space-y-2">
                              {o.items.map((i) => (
                                <div key={i.product_id} className="text-sm flex justify-between">
                                  <span className="truncate">{i.name} × {i.quantity}</span>
                                  <span className="font-mono-tmf font-bold">{currencySymbol}{(i.subtotal || i.price * i.quantity).toLocaleString("en-IN")}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 mb-2">Fulfillment Actions</div>
                            <div className="space-y-2">
                              <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.order_id, v)}>
                                <SelectTrigger data-testid={`order-status-${o.order_id}`} className="rounded-none h-9"><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none">
                                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <div className="grid grid-cols-2 gap-2">
                                <Button size="sm" data-testid={`approve-${o.order_id}`} onClick={() => updateOrderStatus(o.order_id, "approved")} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase text-xs font-bold tracking-widest">Approve</Button>
                                <Button size="sm" data-testid={`ship-${o.order_id}`} onClick={() => updateOrderStatus(o.order_id, "shipped")} className="rounded-none bg-tmf-navy hover:bg-tmf-blue uppercase text-xs font-bold tracking-widest">Ship</Button>
                                <Button size="sm" data-testid={`deliver-${o.order_id}`} onClick={() => updateOrderStatus(o.order_id, "delivered")} className="rounded-none bg-green-700 hover:bg-green-800 uppercase text-xs font-bold tracking-widest">Deliver</Button>
                                <Button size="sm" data-testid={`cancel-${o.order_id}`} onClick={() => updateOrderStatus(o.order_id, "cancelled")} variant="outline" className="rounded-none uppercase text-xs font-bold tracking-widest border-tmf-orange text-tmf-orange hover:bg-tmf-orange hover:text-white">Cancel</Button>
                              </div>
                              <Button size="sm" data-testid={`edit-order-customer-${o.order_id}`} onClick={() => setEditingOrderCust({
                                order_id: o.order_id,
                                email: o.email || "",
                                name: o.shipping?.name || "",
                                phone: o.shipping?.phone || "",
                                address_line1: o.shipping?.address_line1 || "",
                                address_line2: o.shipping?.address_line2 || "",
                                city: o.shipping?.city || "",
                                state: o.shipping?.state || "",
                                postal_code: o.shipping?.postal_code || "",
                                country: o.shipping?.country || "India",
                              })} variant="outline" className="rounded-none w-full uppercase text-[10px] font-bold tracking-widest border-slate-400 text-tmf-navy hover:bg-tmf-navy hover:text-white">
                                <Pencil size={12} className="mr-1" /> Edit Customer Info
                              </Button>
                              <Button size="sm" data-testid={`delete-order-${o.order_id}`} onClick={() => deleteOrder(o.order_id)} variant="outline" className="rounded-none w-full uppercase text-[10px] font-bold tracking-widest border-red-500 text-red-600 hover:bg-red-600 hover:text-white">
                                <Trash2 size={12} className="mr-1" /> Delete Order
                              </Button>

                              {/* Shiprocket controls */}
                              {o.payment_status === "paid" && (
                                <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                                  <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue font-bold">Shiprocket</div>
                                  {!o.shiprocket?.awb ? (
                                    <Button size="sm" data-testid={`shiprocket-ship-${o.order_id}`} onClick={() => shipViaShiprocket(o.order_id)}
                                      className="rounded-none w-full bg-indigo-700 hover:bg-indigo-800 uppercase text-[10px] font-bold tracking-widest">
                                      Push to Shiprocket
                                    </Button>
                                  ) : (
                                    <div className="space-y-1 text-[10px] font-mono-tmf text-slate-600">
                                      <div><span className="uppercase tracking-widest text-slate-400">Courier:</span> <span className="font-bold text-tmf-navy uppercase">{o.shiprocket.courier_name || "—"}</span></div>
                                      <div className="break-all"><span className="uppercase tracking-widest text-slate-400">AWB:</span> <span className="font-bold">{o.shiprocket.awb}</span></div>
                                      {o.shiprocket.raw_status && <div><span className="uppercase tracking-widest text-slate-400">Status:</span> <span className="font-bold text-indigo-700 uppercase">{o.shiprocket.raw_status}</span></div>}
                                      <div className="flex gap-1 pt-1">
                                        <Button size="sm" data-testid={`shiprocket-track-${o.order_id}`} onClick={() => refreshTracking(o.order_id)}
                                          variant="outline" className="rounded-none flex-1 uppercase text-[10px] font-bold tracking-widest border-indigo-500 text-indigo-700 hover:bg-indigo-50">
                                          Refresh Tracking
                                        </Button>
                                        {o.shiprocket.tracking_url && (
                                          <a href={o.shiprocket.tracking_url} target="_blank" rel="noreferrer"
                                            className="inline-flex items-center px-2 border border-slate-300 uppercase text-[10px] font-bold tracking-widest hover:bg-slate-100">
                                            Open
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Shiprocket Cargo controls (heavy shipments) */}
                                  <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-orange font-bold pt-2">Shiprocket Cargo</div>
                                  {!o.shiprocket_cargo?.cargo_order_id ? (
                                    <Button size="sm" data-testid={`cargo-ship-${o.order_id}`} onClick={() => shipViaCargo(o.order_id)}
                                      variant="outline"
                                      className="rounded-none w-full border-tmf-orange text-tmf-orange hover:bg-tmf-orange hover:text-white uppercase text-[10px] font-bold tracking-widest">
                                      Push to Cargo (Heavy)
                                    </Button>
                                  ) : (
                                    <div className="space-y-1 text-[10px] font-mono-tmf text-slate-600">
                                      <div><span className="uppercase tracking-widest text-slate-400">Cargo Order:</span> <span className="font-bold">#{o.shiprocket_cargo.cargo_order_id}</span></div>
                                      {o.shiprocket_cargo.waybill_no && <div className="break-all"><span className="uppercase tracking-widest text-slate-400">Waybill:</span> <span className="font-bold">{o.shiprocket_cargo.waybill_no}</span></div>}
                                      <div><span className="uppercase tracking-widest text-slate-400">Status:</span> <span className="font-bold text-tmf-orange uppercase">{o.shiprocket_cargo.status || "created"}</span></div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {(o.razorpay_payment_id || o.session_id || o.gateway) && (
                                <div className="mt-3 pt-3 border-t border-slate-200 text-[10px] font-mono-tmf text-slate-600 space-y-1">
                                  {o.gateway && <div><span className="uppercase tracking-widest text-slate-400">Gateway:</span> <span className="uppercase font-bold">{o.gateway}</span></div>}
                                  {o.razorpay_payment_id && <div className="break-all"><span className="uppercase tracking-widest text-slate-400">RZ Payment:</span> <span className="font-bold">{o.razorpay_payment_id}</span></div>}
                                  {o.session_id && !o.razorpay_payment_id && <div className="break-all"><span className="uppercase tracking-widest text-slate-400">Stripe Session:</span> <span className="font-bold">{o.session_id}</span></div>}
                                  {o.refund?.refund_id && <div className="break-all text-purple-700"><span className="uppercase tracking-widest text-slate-400">Refund:</span> <span className="font-bold">{o.refund.refund_id}</span></div>}
                                  {o.refund?.error && <div className="text-red-600"><span className="uppercase tracking-widest text-slate-400">Refund Err:</span> {o.refund.error}</div>}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Customers */}
        <TabsContent value="customers">
          <div className="border border-slate-200 bg-white overflow-x-auto mt-6">
            <table className="w-full text-sm">
              <thead className="bg-tmf-navy text-white">
                <tr>
                  <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Customer</th>
                  <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Contact</th>
                  <th className="text-left px-3 py-2 font-display uppercase tracking-widest text-xs">Role</th>
                  <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Orders</th>
                  <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Lifetime Spend</th>
                  <th className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.user_id} data-testid={`customer-${c.user_id}`} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-bold text-tmf-navy">{c.name}</div>
                      <div className="text-[10px] font-mono-tmf text-slate-500 uppercase">{c.user_id}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <div>{c.email}</div>
                      {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
                      {c.company && <div className="text-xs text-slate-500">{c.company}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-mono-tmf uppercase px-2 py-1 ${c.role === "admin" ? "bg-blue-100 text-tmf-blue" : "bg-slate-100 text-slate-700"}`}>{c.role}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-tmf font-bold">{c.order_count || 0}</td>
                    <td className="px-3 py-2 text-right font-mono-tmf font-bold">{currencySymbol}{Number(c.lifetime_spend || 0).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <button data-testid={`edit-customer-${c.user_id}`} onClick={() => { setEditingCustomer({ ...c }); setCustDialogOpen(true); }} className="p-2 hover:bg-tmf-navy hover:text-white"><Pencil size={14} /></button>
                      {c.role !== "admin" && (
                        <button data-testid={`delete-customer-${c.user_id}`} onClick={() => deleteCustomer(c.user_id, c.name)} className="p-2 hover:bg-red-600 hover:text-white"><Trash2 size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {customers.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No customers yet.</div>}
          </div>
        </TabsContent>

        {/* Messages / Enquiries */}
        <TabsContent value="messages">
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue">Enquiries & Contact Form</div>
                <div className="font-display font-black text-2xl text-tmf-navy">{enquiries.length} total · {enquiries.filter((e) => e.status === "new").length} new</div>
              </div>
              <div className="flex gap-1">
                {["all", "new", "read", "replied", "archived"].map((s) => (
                  <button
                    key={s}
                    data-testid={`enq-filter-${s}`}
                    onClick={() => setEnquiryFilter(s)}
                    className={`px-3 py-1.5 uppercase tracking-widest text-[10px] font-bold border ${
                      enquiryFilter === s ? "bg-tmf-navy text-white border-tmf-navy" : "bg-white text-slate-600 border-slate-300 hover:border-tmf-navy"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="border border-slate-200 bg-white divide-y divide-slate-200">
              {enquiries
                .filter((e) => enquiryFilter === "all" || e.status === enquiryFilter)
                .map((e) => (
                  <div key={e.enquiry_id} data-testid={`enquiry-${e.enquiry_id}`} className={`p-5 ${e.status === "new" ? "bg-amber-50/40" : ""}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-display font-bold text-tmf-navy">{e.name}</div>
                          <span className={`text-[10px] font-mono-tmf uppercase tracking-widest px-2 py-0.5 ${
                            e.status === "new" ? "bg-red-100 text-red-700" :
                            e.status === "read" ? "bg-sky-100 text-sky-700" :
                            e.status === "replied" ? "bg-green-100 text-green-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>{e.status}</span>
                          <span className="text-[10px] font-mono-tmf uppercase tracking-widest bg-slate-100 text-slate-600 px-2 py-0.5">{e.source}</span>
                          <span className="text-[10px] font-mono-tmf text-slate-400">{new Date(e.created_at).toLocaleString()}</span>
                        </div>
                        <div className="mt-2 text-sm text-slate-700 flex flex-wrap gap-x-6 gap-y-1">
                          <a href={`mailto:${e.email}`} className="text-tmf-blue hover:underline">{e.email}</a>
                          {e.phone && <a href={`tel:${e.phone}`} className="text-tmf-blue hover:underline">{e.phone}</a>}
                          {e.organization && <span className="text-slate-500">{e.organization}</span>}
                        </div>
                        <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{e.message}</p>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <a
                          data-testid={`enq-reply-${e.enquiry_id}`}
                          href={`mailto:${e.email}?subject=Re:%20Your%20enquiry%20to%20TMF%20by%20Silver%20Sure&body=Hi%20${encodeURIComponent(e.name)},%0A%0A`}
                          onClick={() => setEnquiryStatus(e.enquiry_id, "replied")}
                          className="inline-flex justify-center px-3 py-1.5 border border-tmf-blue text-tmf-blue hover:bg-tmf-blue hover:text-white uppercase tracking-widest text-[10px] font-bold"
                        >
                          Reply
                        </a>
                        {e.status === "new" && (
                          <button data-testid={`enq-read-${e.enquiry_id}`} onClick={() => setEnquiryStatus(e.enquiry_id, "read")} className="px-3 py-1.5 border border-slate-300 hover:bg-slate-100 uppercase tracking-widest text-[10px] font-bold">Mark Read</button>
                        )}
                        <button data-testid={`enq-archive-${e.enquiry_id}`} onClick={() => setEnquiryStatus(e.enquiry_id, "archived")} className="px-3 py-1.5 border border-slate-300 hover:bg-slate-100 uppercase tracking-widest text-[10px] font-bold">Archive</button>
                        <button data-testid={`enq-delete-${e.enquiry_id}`} onClick={() => deleteEnquiry(e.enquiry_id)} className="px-3 py-1.5 border border-red-500 text-red-600 hover:bg-red-600 hover:text-white uppercase tracking-widest text-[10px] font-bold">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              {enquiries.filter((e) => enquiryFilter === "all" || e.status === enquiryFilter).length === 0 && (
                <div className="p-8 text-center text-slate-500 text-sm">No enquiries {enquiryFilter !== "all" ? `with status "${enquiryFilter}"` : "yet"}.</div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Pincodes */}
        <TabsContent value="pincodes">
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-slate-200 bg-white p-6">
              <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-2 flex items-center gap-2">
                <MapPin size={18} className="text-tmf-blue" /> Add Serviceable Pincodes
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Paste 6-digit pincodes separated by comma, space or newline. You can also upload a text/CSV file. Duplicate pincodes are ignored.
              </p>
              <Textarea
                data-testid="pin-textarea"
                rows={10}
                value={pincodesText}
                onChange={(e) => setPincodesText(e.target.value)}
                placeholder={"110001\n110002, 400001\n560001 700001"}
                className="rounded-none border-slate-300 font-mono-tmf"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <input ref={(el) => (window.__pinCsv = el)} type="file" accept=".csv,.txt,text/plain" className="hidden" onChange={onCsvUpload} />
                <Button variant="outline" onClick={() => window.__pinCsv?.click()} className="rounded-none uppercase text-xs font-bold tracking-widest"><Upload size={14} className="mr-1" /> Upload CSV / TXT</Button>
                <Button data-testid="pin-add-btn" onClick={() => bulkAddPincodes(false)} disabled={pinBusy} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase text-xs font-bold tracking-widest">
                  <Plus size={14} className="mr-1" /> Add Pincodes
                </Button>
                <Button data-testid="pin-replace-btn" onClick={() => bulkAddPincodes(true)} disabled={pinBusy} variant="outline" className="rounded-none uppercase text-xs font-bold tracking-widest border-tmf-orange text-tmf-orange hover:bg-tmf-orange hover:text-white">
                  Replace All
                </Button>
              </div>
              <div className="mt-4 text-xs text-slate-500 border-t border-slate-200 pt-3">
                <strong>Tip:</strong> Leave the list empty to enable pan-India serviceability by default. Adding any pincode enables strict gating (only listed pincodes will pass at checkout).
              </div>
            </div>

            <div className="border border-slate-200 bg-white p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy">
                  Serviceable Pincodes
                </div>
                <div className="text-sm font-mono-tmf uppercase tracking-widest text-tmf-blue font-bold" data-testid="pin-total">{pincodeList.total}</div>
              </div>
              {pincodeList.total === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500 border border-dashed border-slate-300">
                  No pincodes configured. Site is currently open to <strong>pan-India</strong> checkout.
                </div>
              ) : (
                <>
                  <div className="max-h-72 overflow-y-auto border border-slate-200">
                    <div className="grid grid-cols-3 gap-px bg-slate-200">
                      {pincodeList.pincodes.map((p) => (
                        <div key={p.pincode} data-testid={`pin-${p.pincode}`} className="bg-white p-2 flex items-center justify-between text-sm">
                          <span className="font-mono-tmf font-bold text-tmf-navy">{p.pincode}</span>
                          <button onClick={() => removePincode(p.pincode)} className="text-slate-400 hover:text-tmf-orange"><Trash2 size={12} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button variant="outline" onClick={clearAllPincodes} className="mt-3 rounded-none uppercase text-xs font-bold tracking-widest border-slate-400 text-slate-600">
                    <Trash2 size={14} className="mr-1" /> Clear All
                  </Button>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Site Content CMS */}
        <TabsContent value="content">
          {!contentDraft ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading content…</div>
          ) : (
            <div className="mt-6 space-y-8">
              <div className="flex justify-between items-center border-b border-slate-200 pb-4">
                <div className="text-sm text-slate-600">Edit every headline, image URL, badge, contact detail, and CTA across the site. Changes go live immediately.</div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={resetContent} disabled={contentBusy} className="rounded-none uppercase tracking-widest text-xs font-bold border-slate-400"><RotateCcw size={14} className="mr-1" /> Reset</Button>
                  <Button data-testid="save-content-btn" onClick={saveContent} disabled={contentBusy} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold"><Save size={14} className="mr-1" /> Save Changes</Button>
                </div>
              </div>

              {/* Brand */}
              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">Brand & Header</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Brand Name" path="brand.name" />
                  <InputRow label="Tagline" path="brand.tagline" />
                  <InputRow label="Company Legal Name" path="brand.company" />
                  <ImageInput
                    label="Logo (circular)"
                    value={contentDraft.brand?.logo_url || ""}
                    onChange={(v) => setPath("brand.logo_url")(v)}
                    testId="cms-logo"
                  />
                  <div className="md:col-span-2"><InputRow label="Top Ribbon Text" path="top_ribbon" /></div>
                </div>
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">Hero Section</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Eyebrow" path="hero.eyebrow" />
                  <InputRow label="Title Line 1" path="hero.title_line1" />
                  <InputRow label="Title Line 2 (accent)" path="hero.title_line2" />
                  <InputRow label="Primary CTA Label" path="hero.cta_primary_label" />
                  <InputRow label="Secondary CTA Label" path="hero.cta_secondary_label" />
                  <InputRow label="Warranty Badge Label" path="hero.warranty_label" />
                  <ImageInput label="Hero Image 1" value={contentDraft.hero?.image_1 || ""} onChange={setPath("hero.image_1")} testId="cms-hero-1" />
                  <ImageInput label="Hero Image 2" value={contentDraft.hero?.image_2 || ""} onChange={setPath("hero.image_2")} testId="cms-hero-2" />
                  <div className="md:col-span-2"><InputRow label="Subtitle" path="hero.subtitle" textarea rows={2} /></div>
                  <div className="md:col-span-2"><InputRow label="Warranty Note" path="hero.warranty_note" /></div>
                </div>
                <div className="mt-4">
                  <div className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500 mb-2">Hero Stats</div>
                  <div className="grid grid-cols-3 gap-3">
                    {(contentDraft.hero?.stats || []).map((s, i) => (
                      <div key={i} className="border border-slate-200 p-3 space-y-2">
                        <Input value={s.n} onChange={(e) => setArrayField("hero.stats", i, "n", e.target.value)} placeholder="Number" className="rounded-none" />
                        <Input value={s.l} onChange={(e) => setArrayField("hero.stats", i, "l", e.target.value)} placeholder="Label" className="rounded-none" />
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Category tile images */}
              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">Category Tile Images (Homepage)</div>
                <div className="text-sm text-slate-600 mb-4">Set the background image shown on each category tile in the "Shop by Category" grid on the home page.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categories.map((c) => (
                    <ImageInput
                      key={c.slug}
                      label={`${c.name} · /${c.slug}`}
                      value={contentDraft.category_images?.[c.slug] || ""}
                      onChange={setPath(`category_images.${c.slug}`)}
                      testId={`cms-cat-${c.slug}`}
                    />
                  ))}
                </div>
              </section>

              {/* Other page images */}
              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">About & Contact Page Banners</div>
                <div className="text-sm text-slate-600 mb-4">Optional wide banner images shown at the top of the About and Contact pages (leave blank to hide).</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <ImageInput label="About Page Banner" value={contentDraft.page_images?.about_hero || ""} onChange={setPath("page_images.about_hero")} testId="cms-about-hero" />
                  <ImageInput label="Contact Page Banner" value={contentDraft.page_images?.contact_hero || ""} onChange={setPath("page_images.contact_hero")} testId="cms-contact-hero" />
                </div>
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">Credibility Section</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Eyebrow" path="credibility.eyebrow" />
                  <InputRow label="Title" path="credibility.title" />
                  <div className="md:col-span-2"><InputRow label="Subtitle" path="credibility.subtitle" textarea rows={2} /></div>
                </div>
                <div className="mt-4">
                  <div className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500 mb-2">Certification Badges (leave image blank for icon)</div>
                  <div className="space-y-3">
                    {(contentDraft.credibility?.badges || []).map((b, i) => (
                      <div key={i} className="border border-slate-200 p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                        <div className="space-y-2">
                          <Input value={b.name} onChange={(e) => setArrayField("credibility.badges", i, "name", e.target.value)} placeholder="Name (e.g. ISO 13485)" className="rounded-none" />
                          <Input value={b.note} onChange={(e) => setArrayField("credibility.badges", i, "note", e.target.value)} placeholder="Short description" className="rounded-none" />
                        </div>
                        <div className="md:col-span-2">
                          <ImageInput
                            label="Badge Logo (optional — blank shows icon)"
                            value={b.image || ""}
                            onChange={(v) => setArrayField("credibility.badges", i, "image", v)}
                            testId={`cms-badge-${i}`}
                            compact
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">Contact & WhatsApp</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Email (public)" path="contact.email" />
                  <InputRow label="Phone (leave blank to hide)" path="contact.phone" />
                  <InputRow label="WhatsApp Number (digits only, with country code)" path="contact.whatsapp_number" />
                  <InputRow label="WhatsApp Pre-filled Message" path="contact.whatsapp_message" />
                  <div className="md:col-span-2"><InputRow label="Address" path="contact.address" /></div>
                  <div className="md:col-span-2"><InputRow label="Admin Notification Email (comma-separated) — receives new order alerts" path="contact.admin_notify_email" /></div>
                </div>
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">Footer</div>
                <InputRow label="Footer Tagline" path="footer.tagline" textarea rows={2} />
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">About Page</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Eyebrow" path="about.eyebrow" />
                  <InputRow label="Title" path="about.title" />
                </div>
                <div className="mt-4 space-y-3">
                  <InputRow label="Intro (bold lead)" path="about.intro" />
                  <InputRow label="Body (double newline = paragraph break)" path="about.body" textarea rows={8} />
                </div>
                {/* Stats editor — sqft, countries served, etc. */}
                <div className="mt-6">
                  <div className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500 mb-2">Stats (4 tiles: Manufacturing, Craftspeople, Certification, Countries Served)</div>
                  <div className="grid gap-3">
                    {(contentDraft.about?.stats || []).map((s, i) => (
                      <div key={`about-stat-${i}`} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
                        <Input data-testid={`about-stat-n-${i}`} value={s.n || ""} onChange={(e) => setArrayField("about.stats", i, "n", e.target.value)} placeholder={i === 0 ? "50k sqft" : i === 1 ? "150+" : i === 2 ? "ISO 13485" : "12+"} className="rounded-none" />
                        <Input data-testid={`about-stat-l-${i}`} value={s.l || ""} onChange={(e) => setArrayField("about.stats", i, "l", e.target.value)} placeholder={i === 0 ? "Manufacturing" : i === 1 ? "Craftspeople" : i === 2 ? "Aligned" : "Countries served"} className="rounded-none" />
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-4 pb-2 border-b border-slate-200">Bulk Quote CTA</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Eyebrow" path="bulk_cta.eyebrow" />
                  <InputRow label="Title" path="bulk_cta.title" />
                  <InputRow label="CTA Label" path="bulk_cta.cta_label" />
                  <InputRow label="Small Note" path="bulk_cta.note" />
                  <div className="md:col-span-2"><InputRow label="Subtitle" path="bulk_cta.subtitle" textarea rows={2} /></div>
                </div>
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-2 pb-2 border-b border-slate-200">Privacy Policy</div>
                <div className="text-[11px] font-mono-tmf text-slate-500 mb-3">Shown on /privacy-policy. Use <b>**bold**</b> for section headings; leave a blank line between paragraphs.</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Title" path="privacy_policy.title" />
                  <InputRow label="Last Updated (YYYY-MM-DD)" path="privacy_policy.updated_at" />
                  <div className="md:col-span-2"><InputRow label="Body" path="privacy_policy.body" textarea rows={14} /></div>
                </div>
              </section>

              <section className="border border-slate-200 p-6 bg-white">
                <div className="font-display font-bold uppercase tracking-tight text-lg text-tmf-navy mb-2 pb-2 border-b border-slate-200">Terms of Service</div>
                <div className="text-[11px] font-mono-tmf text-slate-500 mb-3">Shown on /terms-of-service. Use <b>**bold**</b> for section headings; leave a blank line between paragraphs.</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputRow label="Title" path="terms_of_service.title" />
                  <InputRow label="Last Updated (YYYY-MM-DD)" path="terms_of_service.updated_at" />
                  <div className="md:col-span-2"><InputRow label="Body" path="terms_of_service.body" textarea rows={14} /></div>
                </div>
              </section>

              <div className="flex justify-end gap-2 pb-8">
                <Button variant="outline" onClick={resetContent} disabled={contentBusy} className="rounded-none uppercase tracking-widest text-xs font-bold"><RotateCcw size={14} className="mr-1" /> Reset</Button>
                <Button onClick={saveContent} disabled={contentBusy} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold"><Save size={14} className="mr-1" /> Save Changes</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations">
          <div className="mt-6 space-y-6">
            <div className="border-b border-slate-200 pb-4">
              <div className="text-sm text-slate-600 flex items-center gap-2">
                <Puzzle size={16} className="text-tmf-blue" />
                Configure API keys, secrets, and settings for each integration. Sensitive fields are stored encrypted-at-rest and masked in the UI.
              </div>
            </div>

            {["payments", "shipping", "messaging", "database", "sales_channels", "analytics", "compliance"].map((cat) => {
              const list = integrations.filter((i) => i.category === cat);
              if (!list.length) return null;
              return (
                <div key={cat}>
                  <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-3">{cat.replace(/_/g, " ")}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {list.map((it) => {
                      const isConnected = it.status === "connected";
                      const isConfigured = it.status === "configured";
                      return (
                        <div key={it.id} data-testid={`integration-${it.id}`} className="border border-slate-200 bg-white p-5 flex flex-col brutal-shadow-hover">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="font-display font-bold text-tmf-navy uppercase tracking-tight text-base leading-tight">{it.name}</div>
                            {isConnected && (
                              <span className="flex items-center gap-1 text-[10px] font-mono-tmf uppercase tracking-widest text-green-700 bg-green-50 border border-green-200 px-2 py-1 shrink-0">
                                <CheckCircle2 size={12} /> Connected
                              </span>
                            )}
                            {isConfigured && (
                              <span className="flex items-center gap-1 text-[10px] font-mono-tmf uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 shrink-0">
                                <Circle size={12} /> Configured
                              </span>
                            )}
                            {!isConnected && !isConfigured && (
                              <span className="flex items-center gap-1 text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 bg-slate-50 border border-slate-200 px-2 py-1 shrink-0">
                                <Circle size={12} /> Available
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed flex-1">{it.note}</p>
                          {(it.fields || []).length > 0 && (
                            <div className="mt-4 space-y-2">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  data-testid={`configure-${it.id}`}
                                  onClick={() => openIntegration(it)}
                                  className="rounded-none bg-tmf-blue hover:bg-tmf-navy text-white uppercase text-xs font-bold tracking-widest flex-1"
                                >
                                  {isConfigured || isConnected ? "Edit Settings" : "Configure"}
                                </Button>
                                {(isConfigured || isConnected) && !it.auto_connected && (
                                  <button data-testid={`reset-${it.id}`} onClick={() => resetIntegration(it.id)} className="px-2 border border-slate-300 hover:bg-slate-50" title="Reset">
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                              {!it.auto_connected && (it.configured || isConfigured || isConnected) && (
                                <Button
                                  size="sm"
                                  data-testid={`toggle-${it.id}`}
                                  onClick={() => toggleIntegration(it.id, it.enabled)}
                                  variant="outline"
                                  className={`rounded-none w-full uppercase text-xs font-bold tracking-widest border ${
                                    it.enabled
                                      ? "border-red-500 text-red-600 hover:bg-red-600 hover:text-white"
                                      : "border-green-600 text-green-700 hover:bg-green-600 hover:text-white"
                                  }`}
                                >
                                  {it.enabled ? "Disconnect" : "Connect"}
                                </Button>
                              )}
                            </div>
                          )}
                          {it.auto_connected && (
                            <div className="mt-4 text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Auto-configured via environment</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Integration Config Dialog */}
      <Dialog open={!!integDialog} onOpenChange={(o) => { if (!o) setIntegDialog(null); }}>
        <DialogContent className="rounded-none max-w-xl border-tmf-navy">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{integDialog?.name}</DialogTitle>
          </DialogHeader>
          {integDialog && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-sm text-slate-600 leading-relaxed border-l-2 border-tmf-blue pl-3">{integDialog.note}</p>

              <label className="flex items-center gap-3 border border-slate-200 p-3 cursor-pointer hover:border-tmf-blue">
                <input
                  type="checkbox"
                  data-testid="integ-enable"
                  checked={!!integForm.__enabled}
                  onChange={(e) => setIntegForm({ ...integForm, __enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <div className="font-bold text-tmf-navy">Enable {integDialog.name}</div>
                  <div className="text-xs text-slate-500">Turn this integration on once all required fields are filled.</div>
                </div>
              </label>

              {(integDialog.fields || []).map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">
                    {f.label}
                    {f.secret && <span className="ml-2 text-tmf-orange">🔒 encrypted</span>}
                  </label>
                  {f.type === "textarea" ? (
                    <Textarea
                      data-testid={`integ-${f.key}`}
                      rows={3}
                      value={integForm[f.key] || ""}
                      onChange={(e) => setIntegForm({ ...integForm, [f.key]: e.target.value })}
                      className="rounded-none mt-1"
                    />
                  ) : f.type === "select" ? (
                    <Select value={integForm[f.key] || ""} onValueChange={(v) => setIntegForm({ ...integForm, [f.key]: v })}>
                      <SelectTrigger data-testid={`integ-${f.key}`} className="rounded-none mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      data-testid={`integ-${f.key}`}
                      type={f.type === "password" ? "password" : "text"}
                      value={integForm[f.key] || ""}
                      onChange={(e) => setIntegForm({ ...integForm, [f.key]: e.target.value })}
                      className="rounded-none mt-1"
                      placeholder={f.help || ""}
                    />
                  )}
                  {f.help && <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 mt-1">{f.help}</div>}
                </div>
              ))}
            </div>
          )}
          {(() => {
            const testEndpoints = {
              shiprocket: "/admin/shiprocket/test",
              shiprocket_cargo: "/admin/shiprocket_cargo/test",
              google_analytics: "/admin/google_analytics/test",
              meta_pixel: "/admin/meta_pixel/test",
            };
            const testEndpoint = testEndpoints[integDialog?.id];
            const helperText = {
              shiprocket_cargo: "Cargo credentials verify separately. Rates merge with standard Shiprocket at checkout.",
              google_analytics: "Save the ID and toggle Enabled. Test verifies the ID format and pings Google's gtag loader. The tag is auto-injected into every page after you redeploy the frontend.",
              meta_pixel: "Save the Pixel ID and toggle Enabled. Test verifies the format. Pixel auto-fires on every page after frontend redeploy.",
            }[integDialog?.id] || "Save keys first, then click Test Connection to verify auth + pickup location without pushing a real order.";
            if (!testEndpoint) return null;
            return (
              <div className="border border-slate-200 bg-slate-50 p-3 mt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue font-bold">Diagnostic</div>
                  <Button
                    size="sm"
                    data-testid={`test-connection-btn-${integDialog.id}`}
                    disabled={srTesting}
                    onClick={() => testShiprocket(testEndpoint)}
                    variant="outline"
                    className="rounded-none border-indigo-500 text-indigo-700 hover:bg-indigo-50 uppercase text-[10px] font-bold tracking-widest"
                  >
                    {srTesting ? "Testing…" : "Test Connection"}
                  </Button>
                </div>
                <div className="text-[11px] text-slate-600">{helperText}</div>
                {srDiag && (
                  <div data-testid={`test-connection-result-${integDialog.id}`} className={`text-[11px] p-3 border-l-4 ${srDiag.ok ? "bg-green-50 border-green-500 text-green-900" : "bg-red-50 border-red-500 text-red-900"}`}>
                    <div className="font-bold uppercase tracking-widest text-[10px] mb-1">
                      {srDiag.ok ? "✓ Connected" : `✗ Failed at step: ${srDiag.step}`}
                    </div>
                    <div className="whitespace-pre-wrap break-words">{srDiag.message}</div>
                    {srDiag.available_pickups?.length > 0 && (
                      <div className="mt-2 text-[10px]">
                        <b>Available pickups:</b> {srDiag.available_pickups.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setIntegDialog(null)}>Cancel</Button>
            <Button data-testid="save-integ-btn" onClick={saveIntegration} disabled={integBusy} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold">
              <Save size={14} className="mr-1" /> {integBusy ? "Saving…" : "Save Integration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl border-tmf-navy">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{editing?.product_id ? "Edit Product" : "New Product"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Name</label>
                <Input data-testid="prod-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Category</label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Stock</label>
                  <Input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Factory Price ({currencySymbol})</label>
                  <Input data-testid="prod-price" type="number" value={editing.factory_price} onChange={(e) => setEditing({ ...editing, factory_price: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">MRP ({currencySymbol})</label>
                  <Input type="number" value={editing.mrp} onChange={(e) => setEditing({ ...editing, mrp: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>

              {/* Image upload + URL */}
              <ImageInput
                label="Product Image"
                value={editing.image}
                onChange={(v) => setEditing({ ...editing, image: v })}
                testId="prod-image"
              />

              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Description</label>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-none mt-1" rows={4} />
              </div>

              {/* Shipping dimensions (Shiprocket) */}
              <div className="border border-slate-200 p-3 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue font-bold">Shipping Dimensions</div>
                  <div className="text-[10px] font-mono-tmf text-slate-500">(used by Shiprocket to book the courier)</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Weight (kg)</label>
                    <Input data-testid="prod-weight" type="number" step="0.1" value={editing.weight_kg ?? 0} onChange={(e) => setEditing({ ...editing, weight_kg: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Length (cm)</label>
                    <Input data-testid="prod-length" type="number" step="0.1" value={editing.length_cm ?? 0} onChange={(e) => setEditing({ ...editing, length_cm: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Width (cm)</label>
                    <Input data-testid="prod-width" type="number" step="0.1" value={editing.width_cm ?? 0} onChange={(e) => setEditing({ ...editing, width_cm: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Height (cm)</label>
                    <Input data-testid="prod-height" type="number" step="0.1" value={editing.height_cm ?? 0} onChange={(e) => setEditing({ ...editing, height_cm: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">HSN Code</label>
                    <Input value={editing.hsn_code ?? "9402"} onChange={(e) => setEditing({ ...editing, hsn_code: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">GST %</label>
                    <Input type="number" step="0.1" value={editing.gst_rate ?? 12} onChange={(e) => setEditing({ ...editing, gst_rate: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Bulk Qty Threshold</label>
                    <Input data-testid="prod-bulk-qty" type="number" min="0" step="1" value={editing.bulk_qty ?? 0} onChange={(e) => setEditing({ ...editing, bulk_qty: e.target.value })} className="rounded-none mt-1" />
                    <div className="text-[10px] text-slate-500 mt-1 font-mono-tmf">Show "Bulk order — sales will contact you" banner when a customer adds this many units or more. Leave 0 for no bulk logic.</div>
                  </div>
                </div>
              </div>

              {/* --- Product Specifications (editable key/value list) --- */}
              <div className="border border-slate-200 p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display font-bold text-tmf-navy uppercase tracking-widest text-sm">Specifications</div>
                  <Button
                    type="button"
                    data-testid="specs-add-row"
                    size="sm"
                    variant="outline"
                    className="rounded-none uppercase tracking-widest text-[10px] font-bold"
                    onClick={() => {
                      const list = [...(editing.specsList || [])];
                      list.push({ id: Math.random().toString(36).slice(2, 10), k: "", v: "" });
                      setEditing({ ...editing, specsList: list });
                    }}
                  >
                    <Plus size={14} className="mr-1" /> Add Row
                  </Button>
                </div>
                <div className="text-[10px] text-slate-500 mb-3 font-mono-tmf">Free-form key/value pairs (e.g. "Frame", "MS Powder-coated"). Displayed on the product detail page. Empty rows are ignored on save; if two rows share the same label, only the last one is kept.</div>
                {(editing.specsList || []).length === 0 ? (
                  <div className="text-xs text-slate-400 italic font-mono-tmf text-center py-4 border border-dashed border-slate-200">No specifications yet — click "Add Row" to start.</div>
                ) : (
                  <div className="space-y-2">
                    {(editing.specsList || []).map((row, idx) => (
                      <div key={row.id} className="grid grid-cols-12 gap-2 items-center">
                        <Input
                          data-testid={`spec-key-${idx}`}
                          value={row.k}
                          placeholder="Label (e.g. Frame)"
                          onChange={(e) => {
                            const list = [...(editing.specsList || [])];
                            list[idx] = { ...list[idx], k: e.target.value };
                            setEditing({ ...editing, specsList: list });
                          }}
                          className="rounded-none col-span-5 text-sm"
                        />
                        <Input
                          data-testid={`spec-value-${idx}`}
                          value={row.v}
                          placeholder="Value (e.g. MS Powder-coated)"
                          onChange={(e) => {
                            const list = [...(editing.specsList || [])];
                            list[idx] = { ...list[idx], v: e.target.value };
                            setEditing({ ...editing, specsList: list });
                          }}
                          className="rounded-none col-span-6 text-sm"
                        />
                        <Button
                          type="button"
                          data-testid={`spec-remove-${idx}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const list = [...(editing.specsList || [])];
                            list.splice(idx, 1);
                            setEditing({ ...editing, specsList: list });
                          }}
                          className="rounded-none col-span-1 h-9 text-red-600 hover:bg-red-50 hover:text-red-700"
                          aria-label="Remove row"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                <span>Featured on homepage</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-product-btn" onClick={saveProduct} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer edit dialog */}
      <Dialog open={custDialogOpen} onOpenChange={setCustDialogOpen}>
        <DialogContent className="rounded-none max-w-3xl border-tmf-navy max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Edit Customer</DialogTitle>
          </DialogHeader>
          {editingCustomer && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Name</label>
                  <Input data-testid="cust-name" value={editingCustomer.name || ""} onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Email (read-only)</label>
                  <Input value={editingCustomer.email} disabled className="rounded-none mt-1 bg-slate-50" />
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Phone</label>
                  <Input value={editingCustomer.phone || ""} onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Company</label>
                  <Input value={editingCustomer.company || ""} onChange={(e) => setEditingCustomer({ ...editingCustomer, company: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Role</label>
                  <Select value={editingCustomer.role || "customer"} onValueChange={(v) => setEditingCustomer({ ...editingCustomer, role: v })}>
                    <SelectTrigger data-testid="cust-role" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="customer">customer</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Internal Notes</label>
                  <Input value={editingCustomer.notes || ""} onChange={(e) => setEditingCustomer({ ...editingCustomer, notes: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>

              {/* Address book */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} className="text-tmf-blue" />
                  <div className="font-display font-bold uppercase tracking-widest text-sm text-tmf-navy">Saved Addresses</div>
                  <span className="text-[10px] font-mono-tmf text-slate-400 ml-auto">{(editingCustomer.addresses || []).length} on file</span>
                </div>
                <div className="space-y-2 mb-4">
                  {(editingCustomer.addresses || []).length === 0 && (
                    <div className="text-xs text-slate-500 italic">No addresses yet. Add one below.</div>
                  )}
                  {(editingCustomer.addresses || []).map((a) => (
                    <div key={a.address_id} data-testid={`address-row-${a.address_id}`} className="border border-slate-200 p-3 grid grid-cols-[1fr_auto] gap-3 items-start">
                      <div className="text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-display font-bold text-tmf-navy uppercase text-[11px] tracking-widest">{a.label || "Address"}</span>
                          {a.is_default && <span className="text-[9px] font-mono-tmf uppercase tracking-widest bg-tmf-blue text-white px-1.5 py-0.5">Default</span>}
                        </div>
                        <div className="text-slate-700">{a.name} · {a.phone}</div>
                        <div className="text-slate-600 mt-1">
                          {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ""}<br />
                          {a.city}, {a.state} - {a.postal_code} · {a.country}
                        </div>
                      </div>
                      <button
                        data-testid={`delete-address-${a.address_id}`}
                        onClick={() => deleteCustomerAddress(a.address_id)}
                        className="p-2 hover:bg-red-600 hover:text-white text-red-600 border border-red-500"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new address */}
                <div className="bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue font-bold mb-2">+ Add New Address</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <Input data-testid="addr-label" placeholder="Label (Home / Office)" value={newAddress.label} onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })} className="rounded-none" />
                    <Input placeholder="Name" value={newAddress.name} onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })} className="rounded-none" />
                    <Input placeholder="Phone" value={newAddress.phone} onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })} className="rounded-none" />
                    <Input data-testid="addr-line1" placeholder="Address Line 1" value={newAddress.address_line1} onChange={(e) => setNewAddress({ ...newAddress, address_line1: e.target.value })} className="rounded-none col-span-2 md:col-span-3" />
                    <Input placeholder="Address Line 2 (optional)" value={newAddress.address_line2} onChange={(e) => setNewAddress({ ...newAddress, address_line2: e.target.value })} className="rounded-none col-span-2 md:col-span-3" />
                    <Input data-testid="addr-city" placeholder="City" value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} className="rounded-none" />
                    <Input placeholder="State" value={newAddress.state} onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })} className="rounded-none" />
                    <Input data-testid="addr-pincode" placeholder="Pincode" value={newAddress.postal_code} onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })} className="rounded-none" />
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={newAddress.is_default} onChange={(e) => setNewAddress({ ...newAddress, is_default: e.target.checked })} />
                      Mark as default
                    </label>
                    <Button data-testid="add-address-btn" size="sm" disabled={addressBusy} onClick={addCustomerAddress} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-[10px] font-bold">
                      <Plus size={12} className="mr-1" /> {addressBusy ? "Adding…" : "Add Address"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setCustDialogOpen(false)}>Close</Button>
            <Button data-testid="save-customer-btn" onClick={saveCustomer} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold">Save Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order customer info edit dialog */}
      <Dialog open={!!editingOrderCust} onOpenChange={(v) => !v && setEditingOrderCust(null)}>
        <DialogContent className="rounded-none max-w-xl border-tmf-navy max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Edit Order Customer Info</DialogTitle>
          </DialogHeader>
          {editingOrderCust && (
            <div className="space-y-3">
              <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Order · {editingOrderCust.order_id}</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Email</label>
                  <Input data-testid="oc-email" type="email" value={editingOrderCust.email || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, email: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Name</label>
                  <Input data-testid="oc-name" value={editingOrderCust.name || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, name: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Phone</label>
                  <Input data-testid="oc-phone" value={editingOrderCust.phone || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, phone: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Country</label>
                  <Input value={editingOrderCust.country || "India"} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, country: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Address Line 1</label>
                  <Input value={editingOrderCust.address_line1 || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, address_line1: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Address Line 2 (optional)</label>
                  <Input value={editingOrderCust.address_line2 || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, address_line2: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">City</label>
                  <Input value={editingOrderCust.city || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, city: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">State</label>
                  <Input value={editingOrderCust.state || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, state: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Pincode</label>
                  <Input value={editingOrderCust.postal_code || ""} onChange={(e) => setEditingOrderCust({ ...editingOrderCust, postal_code: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>
              <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
                Editing customer info on a paid order will change the invoice + shipment address. Do this only if the customer requests a correction BEFORE the order ships.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEditingOrderCust(null)}>Cancel</Button>
            <Button data-testid="save-order-cust-btn" disabled={orderCustBusy} onClick={saveOrderCustomer} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold">
              {orderCustBusy ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category edit dialog */}
      <Dialog open={!!editingCat} onOpenChange={(v) => !v && setEditingCat(null)}>
        <DialogContent className="rounded-none max-w-md border-tmf-navy">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Edit Category</DialogTitle>
          </DialogHeader>
          {editingCat && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Name</label>
                <Input data-testid="edit-cat-name" value={editingCat.name || ""}
                  onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })}
                  className="rounded-none mt-1" />
              </div>
              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Slug (read-only)</label>
                <Input value={editingCat.slug} disabled className="rounded-none mt-1 bg-slate-50 font-mono-tmf" />
              </div>
              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Icon</label>
                <Input value={editingCat.icon || ""}
                  onChange={(e) => setEditingCat({ ...editingCat, icon: e.target.value })}
                  className="rounded-none mt-1" />
              </div>
              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Description</label>
                <Input value={editingCat.description || ""}
                  onChange={(e) => setEditingCat({ ...editingCat, description: e.target.value })}
                  className="rounded-none mt-1" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEditingCat(null)}>Cancel</Button>
            <Button data-testid="save-cat-btn" disabled={catBusy} onClick={saveCategoryEdit}
              className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-xs font-bold">
              {catBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
